/**
 * Shared Availability Search component
 * Used by both lawyer/availability.tsx and tools/availability.tsx
 */
import { useState } from 'react'
import { useNavigate } from '@tanstack/react-router'
import { Plane, Search, Loader2, AlertCircle, ArrowRight, Train, Armchair, Wifi, WifiOff } from 'lucide-react'
import { type AvailabilityResponse, type FlightOption, type FlightSegment, type BookingClass } from '@/utils/api/lawyerAPI'
import { useAmadeusWebSocket, type QueueProgress } from '@/contexts/AmadeusWebSocketContext'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu'

/**
 * Format time from HHMM to HH:MM
 */
function formatTime(time: string): string {
  if (time.length === 4) {
    return `${time.slice(0, 2)}:${time.slice(2)}`
  }
  return time
}

/**
 * Format a date as DDMMM for Amadeus (e.g., "01JAN", "15DEC")
 */
function formatDateForAmadeus(date: Date): string {
  const day = date.getDate().toString().padStart(2, '0')
  const months = ['JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
                  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC']
  const month = months[date.getMonth()]
  return `${day}${month}`
}

/**
 * Render booking classes as compact inline text
 */
function BookingClassesDisplay({ classes }: { classes: BookingClass[] }) {
  return (
    <span className="font-mono text-xs text-slate-700">
      {classes.map((bc, idx) => {
        const seatDisplay = bc.is_waitlist ? 'L' : (bc.seats !== null ? bc.seats.toString() : '')
        const isClosed = bc.seats === 0
        return (
          <span
            key={idx}
            className={isClosed ? 'text-slate-300' : ''}
            title={bc.is_waitlist ? `${bc.code} - Waitlist` : bc.seats !== null ? `${bc.code} - ${bc.seats} seats` : bc.code}
          >
            {bc.code}{seatDisplay}{idx < classes.length - 1 ? ' ' : ''}
          </span>
        )
      })}
    </span>
  )
}

/**
 * Button to open seat map for a flight segment
 */
function SeatMapButton({
  segment,
  date,
  seatmapPath
}: {
  segment: FlightSegment
  date: string
  seatmapPath: string
}) {
  const navigate = useNavigate()

  // Don't show for trains
  if (segment.is_train) return null

  const handleCabinSelect = (cabinClass: string) => {
    navigate({
      to: seatmapPath,
      search: {
        airline: segment.airline,
        flight: segment.flight_number,
        class: cabinClass,
        date,
        origin: segment.departure_airport,
        destination: segment.arrival_airport,
      },
    })
  }

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button
          className="p-1.5 rounded-md hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          title="Sitzplan anzeigen"
        >
          <Armchair className="h-4 w-4" />
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-40">
        <DropdownMenuItem onClick={() => handleCabinSelect('F')}>
          First Class
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleCabinSelect('C')}>
          Business
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleCabinSelect('W')}>
          Premium Economy
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => handleCabinSelect('M')}>
          Economy
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  )
}

/**
 * Render a flight segment row
 */
function SegmentRow({
  segment,
  isConnection,
  isLastSegment,
  isMultiSegment,
  date,
  seatmapPath
}: {
  segment: FlightSegment
  isConnection: boolean
  isLastSegment: boolean
  isMultiSegment: boolean
  date: string
  seatmapPath: string
}) {
  const flightNum = `${segment.airline}${segment.flight_number.padStart(4, ' ')}`

  return (
    <div className={`flex items-center gap-2 py-1.5 ${isConnection ? 'border-t border-slate-100' : ''}`}>
      {/* Flight info - fixed width */}
      <div className="flex items-center gap-1.5 w-[90px] shrink-0">
        {segment.is_train ? (
          <Train className="h-3.5 w-3.5 text-slate-400" />
        ) : (
          <Plane className="h-3.5 w-3.5 text-slate-400" />
        )}
        <span className="font-mono font-semibold text-sm">{flightNum}</span>
        {segment.codeshare_indicator && (
          <span className="text-[10px] text-slate-400">{segment.codeshare_indicator}</span>
        )}
      </div>

      {/* Route - fixed width */}
      <div className="flex items-center gap-1 w-[100px] shrink-0 text-sm">
        <span className="font-medium">{segment.departure_airport}</span>
        <ArrowRight className="h-3 w-3 text-slate-300" />
        <span className="font-medium">{segment.arrival_airport}</span>
      </div>

      {/* Times - fixed width */}
      <div className="w-[95px] shrink-0 text-sm text-slate-500">
        {formatTime(segment.departure_time)}-{formatTime(segment.arrival_time)}
        {segment.arrival_next_day && <span className="text-[10px] text-slate-400">+1</span>}
      </div>

      {/* Duration + Equipment - fixed width */}
      {/* Hide duration on last segment of multi-segment options (shown as total_duration on right) */}
      <div className="w-[60px] shrink-0 text-xs text-slate-400">
        {!(isLastSegment && isMultiSegment) && (segment.duration || '')}
        {segment.equipment && <span className="ml-1">{segment.equipment}</span>}
      </div>

      {/* Booking Classes - flex to fill remaining space, NOW ALIGNED */}
      <div className="flex-1 min-w-0">
        <BookingClassesDisplay classes={segment.booking_classes} />
      </div>

      {/* Seat Map Button */}
      <div className="shrink-0">
        <SeatMapButton segment={segment} date={date} seatmapPath={seatmapPath} />
      </div>
    </div>
  )
}

/**
 * Render a flight option row
 */
function FlightOptionRow({
  option,
  date,
  seatmapPath
}: {
  option: FlightOption
  date: string
  seatmapPath: string
}) {
  return (
    <div className="bg-white border-b border-slate-100 px-3 py-1 hover:bg-slate-50">
      <div className="flex items-start gap-2">
        {/* Option number */}
        <span className="text-xs text-slate-400 font-mono w-5 pt-1.5">{option.option_number}</span>

        {/* Segments */}
        <div className="flex-1">
          {option.segments.map((segment, idx) => (
            <SegmentRow
              key={idx}
              segment={segment}
              isConnection={idx > 0}
              isLastSegment={idx === option.segments.length - 1}
              isMultiSegment={option.segments.length > 1}
              date={date}
              seatmapPath={seatmapPath}
            />
          ))}
        </div>

        {/* Total duration for connections */}
        {option.segments.length > 1 && option.total_duration && (
          <div className="text-xs text-slate-400 pt-1.5 shrink-0">
            {option.total_duration}
          </div>
        )}
      </div>
    </div>
  )
}

/**
 * Convert WebSocket result to AvailabilityResponse format
 */
function convertResult(wsResult: Record<string, unknown>): AvailabilityResponse {
  return {
    command: wsResult.command as string || '',
    raw_response: wsResult.raw_response as string || '',
    origin: wsResult.origin as string,
    destination: wsResult.destination as string,
    destination_city: wsResult.destination_city as string | undefined,
    date: wsResult.date as string | undefined,
    day_of_week: wsResult.day_of_week as string | undefined,
    flights: (wsResult.flights as FlightOption[]) || [],
    has_more: wsResult.has_more as boolean | undefined,
    error: wsResult.parse_error as string | undefined,
  }
}

/**
 * WebSocket status indicator component
 */
function StatusIndicator({ wsStatus }: { wsStatus: string }) {
  const isConnected = wsStatus === 'connected'
  return (
    <div className="flex items-center gap-1.5 text-xs">
      {isConnected ? (
        <>
          <Wifi className="h-3.5 w-3.5 text-green-500" />
          <span className="text-green-600">Verbunden</span>
        </>
      ) : (
        <>
          <WifiOff className="h-3.5 w-3.5 text-amber-500" />
          <span className="text-amber-600">
            {wsStatus === 'connecting' ? 'Verbinde...' : 'Getrennt'}
          </span>
        </>
      )}
    </div>
  )
}

export interface AvailabilitySearchProps {
  /** Path to seatmap page (e.g., '/lawyer/seatmap' or '/tools/seatmap') */
  seatmapPath: string
}

export function AvailabilitySearch({ seatmapPath }: AvailabilitySearchProps) {
  const [origin, setOrigin] = useState('')
  const [destination, setDestination] = useState('')
  const [date, setDate] = useState(formatDateForAmadeus(new Date()))
  const [airline, setAirline] = useState('')
  const [isLoading, setIsLoading] = useState(false)
  const [result, setResult] = useState<AvailabilityResponse | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [queueStatus, setQueueStatus] = useState<string | null>(null)

  // Use the shared Amadeus WebSocket connection
  const { status: wsStatus, sendAvailabilityRequest } = useAmadeusWebSocket()

  const handleProgress = (progress: QueueProgress) => {
    if (progress.type === 'queue_status' && progress.queue_position && progress.queue_position > 1) {
      setQueueStatus(`In Warteschlange (Position ${progress.queue_position})...`)
    } else if (progress.type === 'processing') {
      setQueueStatus('Suche läuft...')
    }
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setError(null)
    setResult(null)

    if (!origin.trim() || origin.trim().length !== 3) {
      setError('Bitte geben Sie einen gültigen Abflughafen-Code ein (3 Buchstaben)')
      return
    }
    if (!destination.trim() || destination.trim().length !== 3) {
      setError('Bitte geben Sie einen gültigen Zielflughafen-Code ein (3 Buchstaben)')
      return
    }
    if (airline && airline.trim().length !== 2) {
      setError('Der Airline-Code muss 2 Zeichen haben (z.B. LH, EW)')
      return
    }

    // Check WebSocket connection
    if (wsStatus !== 'connected') {
      setError('WebSocket nicht verbunden. Bitte warten Sie auf die Verbindung.')
      return
    }

    setIsLoading(true)
    setQueueStatus('In Warteschlange...')

    try {
      const wsResult = await sendAvailabilityRequest(
        {
          origin: origin.trim(),
          destination: destination.trim(),
          date: date.trim() || undefined,
          airline: airline.trim() || undefined,
        },
        handleProgress
      )

      const response = convertResult(wsResult as Record<string, unknown>)

      if (response.error) {
        setError(response.error)
      } else {
        setResult(response)
      }
    } catch (err: unknown) {
      const errorMessage = err instanceof Error ? err.message : 'Unbekannter Fehler'
      setError(errorMessage)
    } finally {
      setIsLoading(false)
      setQueueStatus(null)
    }
  }

  return (
    <div className="max-w-4xl">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <Plane className="h-8 w-8 text-action-500" />
          <div>
            <h1 className="text-2xl lg:text-3xl font-bold text-slate-900">Verfügbarkeit</h1>
            <p className="text-slate-500">Flugverfügbarkeit und Buchungsklassen abfragen</p>
          </div>
        </div>
        <StatusIndicator wsStatus={wsStatus} />
      </div>

      {/* Search Form */}
      <div className="bg-white rounded-xl border border-slate-200 shadow-sm p-6 mb-6">
        <form onSubmit={handleSubmit} className="flex flex-col md:flex-row gap-4 items-end flex-wrap">
          <div className="w-full md:w-24">
            <Label htmlFor="origin" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Von
            </Label>
            <Input
              id="origin"
              type="text"
              placeholder="FRA"
              value={origin}
              onChange={(e) => setOrigin(e.target.value.toUpperCase())}
              maxLength={3}
              className="uppercase w-full font-mono"
            />
          </div>

          <div className="w-full md:w-24">
            <Label htmlFor="destination" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Nach
            </Label>
            <Input
              id="destination"
              type="text"
              placeholder="JFK"
              value={destination}
              onChange={(e) => setDestination(e.target.value.toUpperCase())}
              maxLength={3}
              className="uppercase w-full font-mono"
            />
          </div>

          <div className="w-full md:w-28">
            <Label htmlFor="date" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Datum (TTMMM)
            </Label>
            <Input
              id="date"
              type="text"
              placeholder="15JAN"
              value={date}
              onChange={(e) => setDate(e.target.value.toUpperCase())}
              maxLength={5}
              className="uppercase w-full font-mono"
            />
          </div>

          <div className="w-full md:w-24">
            <Label htmlFor="airline" className="text-sm font-medium text-slate-700 mb-1.5 block">
              Airline (optional)
            </Label>
            <Input
              id="airline"
              type="text"
              placeholder="LH"
              value={airline}
              onChange={(e) => setAirline(e.target.value.toUpperCase())}
              maxLength={2}
              className="uppercase w-full font-mono"
            />
          </div>

          <Button
            type="submit"
            disabled={isLoading || wsStatus !== 'connected'}
            className="w-full md:w-auto"
          >
            {isLoading ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                {queueStatus || 'Suche...'}
              </>
            ) : wsStatus !== 'connected' ? (
              <>
                <WifiOff className="h-4 w-4 mr-2" />
                Verbinde...
              </>
            ) : (
              <>
                <Search className="h-4 w-4 mr-2" />
                Suchen
              </>
            )}
          </Button>
        </form>

        <p className="text-xs text-slate-400 mt-3">
          Beispiel: FRA → JFK am 15JAN mit LH → Befehl: AN15JANFRAJFK/ALH
        </p>
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertCircle className="h-5 w-5 text-red-500 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-red-800 font-medium">Fehler</p>
            <p className="text-red-700 text-sm">{error}</p>
          </div>
        </div>
      )}

      {/* Results */}
      {result && (
        <div className="flex-1 overflow-auto">
          {/* Results Header */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <h2 className="text-base font-semibold text-slate-900">
                {result.origin} → {result.destination}
              </h2>
              {result.destination_city && (
                <span className="text-sm text-slate-500">({result.destination_city})</span>
              )}
              {result.date && (
                <span className="text-xs text-slate-500 bg-slate-100 px-2 py-0.5 rounded">
                  {result.day_of_week} {result.date}
                </span>
              )}
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs text-slate-500">
                {result.flights.length} Optionen
              </span>
              {result.has_more && (
                <span className="text-[10px] text-amber-600 bg-amber-50 px-1.5 py-0.5 rounded">
                  +mehr
                </span>
              )}
              <code className="text-[10px] bg-slate-100 px-1.5 py-0.5 rounded text-slate-500 font-mono">
                {result.command}
              </code>
            </div>
          </div>

          {/* Flight Options */}
          {result.flights.length > 0 ? (
            <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
              {result.flights.map((option) => (
                <FlightOptionRow
                  key={option.option_number}
                  option={option}
                  date={result.date || date}
                  seatmapPath={seatmapPath}
                />
              ))}
            </div>
          ) : (
            <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 text-center">
              <Plane className="h-10 w-10 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500 text-sm">Keine Verfügbarkeit gefunden</p>
            </div>
          )}
        </div>
      )}

      {/* Empty state when no query yet */}
      {!result && !error && !isLoading && (
        <div className="bg-slate-50 rounded-lg border border-slate-200 p-6 text-center">
          <Plane className="h-10 w-10 text-slate-300 mx-auto mb-3" />
          <p className="text-slate-500 text-sm">
            Geben Sie Abflug- und Zielflughafen ein, um die Verfügbarkeit abzurufen
          </p>
          <p className="text-xs text-slate-400 mt-1">
            Format: J9 = J-Klasse 9+ Plätze, JL = Warteliste, J0 = geschlossen
          </p>
        </div>
      )}
    </div>
  )
}
